import {ActionReducerMapBuilder, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {RootState} from "../../app/store";
import {APIError} from "../../services/HttpService";
import {listUrls, ListUrlsResult} from "../../services/UrlsService";
import {createDefaultMatchers, SliceStatus} from "../BaseState";
import {RejectError, RejectErrorType} from "../error/Error";
import UrlsState, {AdaptiveType, QueryResult} from "./UrlsState";

const initialState: UrlsState = {
  result: null,
  pendings: [],
  status: SliceStatus.idle,
  error: null
};

interface ListUrlsAction {
  readonly userId: number;
  readonly like?: string;
  readonly regex?: string;
  readonly adaptiveType?: AdaptiveType;
  readonly limit: number;
  readonly offset: number;
}

export const list = createAsyncThunk<QueryResult, ListUrlsAction, {rejectValue: RejectError}>(
  "users/urls/list",
  async (action: ListUrlsAction, {rejectWithValue}) => {
    const {userId, limit, offset, like, regex, adaptiveType} = action;
    try {
      const result: ListUrlsResult = await listUrls(userId, limit, offset, like, regex, adaptiveType);

      return {
        query: {
          like: like,
          regex: regex,
          adaptiveType: adaptiveType,
          limit: limit,
          offset: offset
        },
        urls: result.urls,
        count: result.count ?? 0
      };
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  },
  {
    condition: (action: ListUrlsAction, {getState}: {getState: RootState}): boolean => {
      const state = getState().urls as UrlsState;
      if (state.status === SliceStatus.loading) {
        return false;
      }

      return true;
    }
  }
);

const createActions = (builder: ActionReducerMapBuilder<UrlsState>) => {
  builder.addCase(list.fulfilled, (draft, action) => {
    draft.result = action.payload;
  });

  createDefaultMatchers(list.typePrefix, builder);
};

export const urlsSlice = createSlice({
  name: "urls",
  initialState,
  reducers: {
    clearListResult: (draft) => {
      draft.result = null;
    }
  },
  extraReducers: createActions
});

export const {clearListResult} = urlsSlice.actions;
export const urlsState = (state: RootState): UrlsState => state.urls;
export default urlsSlice.reducer;
